import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { styled } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { fetchAuditLog, fetchAuditLogFilterOptions } from '@/lib/api';
import { AuditLogEntry, AuditLogFilterOption } from '@/lib/types';
import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';
import SearchTextField from '@/components/SearchTextField/SearchTextField';
import Select from '@/components/Select/Select';
import Table from '@/components/Table/Table';
import { ColumnDef } from '@/components/Table/TableHelpers';
import AuditLogActionChip from './AuditLogActionChip';
import AuditLogPagination from './AuditLogPagination';
import {
  AUDIT_LOG_DATE_FORMAT,
  AUDIT_LOG_MIN_SEARCH_LENGTH,
  AUDIT_LOG_PAGE_SIZE,
  AUDIT_LOG_SEARCH_DEBOUNCE_MS,
  AUDIT_LOG_TIME_FORMAT,
  getInitials,
} from './AuditLogAdminPageHelpers';

const toSelectOptions = (options: AuditLogFilterOption[] = []) =>
  options.map((option) => ({
    id: option.value,
    value: option.value,
    label: option.label,
    text: option.label,
  }));

const AuditLogAdminPage = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const searchRef = useRef('');
  const tableScrollRef = useRef<HTMLDivElement>(null);

  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        const trimmed = value.trim();
        const next =
          trimmed.length >= AUDIT_LOG_MIN_SEARCH_LENGTH
            ? trimmed
            : '';
        if (next === searchRef.current) return;
        searchRef.current = next;
        setSearch(next);
        setPage(1);
      }, AUDIT_LOG_SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  // The table scrolls inside the card, so a new page or query would otherwise
  // open at the previous scroll offset.
  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0 });
  }, [page, search, entityTypes, actions]);

  const { data: filterOptions } = useQuery({
    queryKey: ['auditLogFilterOptions'],
    queryFn: fetchAuditLogFilterOptions,
  });

  const { data, isPending, isFetching, isError } = useQuery({
    queryKey: ['auditLog', page, search, entityTypes, actions],
    queryFn: () =>
      fetchAuditLog({
        offset: (page - 1) * AUDIT_LOG_PAGE_SIZE,
        limit: AUDIT_LOG_PAGE_SIZE,
        search: search || undefined,
        entity_types: entityTypes.length ? entityTypes : undefined,
        actions: actions.length ? actions : undefined,
      }),
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasActiveQuery =
    !!search || entityTypes.length > 0 || actions.length > 0;

  const columns: ColumnDef<AuditLogEntry>[] = [
    {
      id: 'timestamp',
      label: (
        <FormattedMessage
          id="auditLog.column.timestamp"
          defaultMessage="Timestamp"
        />
      ),
      width: '16%',
      sortable: false,
      render: (row) => (
        <>
          <PrimaryText>
            {dayjs(row.created_at).format(AUDIT_LOG_DATE_FORMAT)}
          </PrimaryText>
          <SecondaryText>
            {dayjs(row.created_at).format(AUDIT_LOG_TIME_FORMAT)}
          </SecondaryText>
        </>
      ),
    },
    {
      id: 'actor',
      label: (
        <FormattedMessage
          id="auditLog.column.actor"
          defaultMessage="Actor"
        />
      ),
      width: '26%',
      sortable: false,
      render: (row) => {
        const isDeleted =
          !row.performed_by_name && !row.performed_by_email;
        const name = row.performed_by_name || row.performed_by_email;
        return (
          <ActorRow>
            <Avatar data-deleted={isDeleted}>
              {isDeleted ? '?' : getInitials(name ?? '')}
            </Avatar>
            <CellText>
              {isDeleted ? (
                <DeletedActor>
                  <FormattedMessage
                    id="auditLog.deletedUser"
                    defaultMessage="Deleted user"
                  />
                </DeletedActor>
              ) : (
                <>
                  <PrimaryText>{name}</PrimaryText>
                  {row.performed_by_email ? (
                    <SecondaryText>
                      {row.performed_by_email}
                    </SecondaryText>
                  ) : null}
                </>
              )}
            </CellText>
          </ActorRow>
        );
      },
    },
    {
      id: 'action',
      label: (
        <FormattedMessage
          id="auditLog.column.action"
          defaultMessage="Action"
        />
      ),
      width: '24%',
      sortable: false,
      render: (row) => (
        <AuditLogActionChip
          action={row.action}
          label={row.action_label}
        />
      ),
    },
    {
      id: 'entity',
      label: (
        <FormattedMessage
          id="auditLog.column.entity"
          defaultMessage="Entity"
        />
      ),
      sortable: false,
      render: (row) => (
        <CellText>
          <PrimaryText title={row.entity_name ?? undefined}>
            {row.entity_name ?? '—'}
          </PrimaryText>
          <SecondaryText>{row.entity_type_label}</SecondaryText>
        </CellText>
      ),
    },
  ];

  return (
    <Root>
      <div className="page-width-wrapper">
        <Main>
          <Header>
            <Title>
              <FormattedMessage
                id="auditLog.title"
                defaultMessage="Activity Log"
              />
            </Title>
            <Subtitle>
              <FormattedMessage
                id="auditLog.subtitle"
                defaultMessage="A chronological record of key user actions across the portal."
              />
            </Subtitle>
          </Header>
          <Card>
            <Toolbar>
              <SearchWrapper>
                <SearchTextField
                  placeholder={
                    <FormattedMessage
                      id="auditLog.searchPlaceholder"
                      defaultMessage="Search by date, actor, action, or entity…"
                    />
                  }
                  aria-label="Search activity log"
                  value={searchInput}
                  onChange={(value) => {
                    setSearchInput(value);
                    applySearch(value);
                  }}
                  minQueryLength={AUDIT_LOG_MIN_SEARCH_LENGTH}
                />
                <SearchHint>
                  <FormattedMessage
                    id="auditLog.searchHint"
                    defaultMessage="Type 3+ characters to search"
                  />
                </SearchHint>
              </SearchWrapper>
              <FilterWrapper>
                <Select
                  multiple
                  size="small"
                  aria-label="Filter by entity type"
                  placeholder={
                    <FormattedMessage
                      id="auditLog.allEntityTypes"
                      defaultMessage="All entity types"
                    />
                  }
                  options={toSelectOptions(
                    filterOptions?.entity_types,
                  )}
                  value={entityTypes}
                  onChange={(value) => {
                    setEntityTypes(value as string[]);
                    setPage(1);
                  }}
                />
              </FilterWrapper>
              <FilterWrapper>
                <Select
                  multiple
                  size="small"
                  aria-label="Filter by action"
                  placeholder={
                    <FormattedMessage
                      id="auditLog.allActions"
                      defaultMessage="All actions"
                    />
                  }
                  options={toSelectOptions(filterOptions?.actions)}
                  value={actions}
                  onChange={(value) => {
                    setActions(value as string[]);
                    setPage(1);
                  }}
                />
              </FilterWrapper>
            </Toolbar>
            {isPending ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  py: 6,
                }}
              >
                <CircularProgress />
              </Box>
            ) : isError ? (
              <EmptyState role="alert">
                <FormattedMessage
                  id="auditLog.loadError"
                  defaultMessage="Could not load the activity log. Please try again."
                />
              </EmptyState>
            ) : total === 0 ? (
              <EmptyState>
                {hasActiveQuery ? (
                  <FormattedMessage
                    id="auditLog.noMatch"
                    defaultMessage="No activity matches your search"
                  />
                ) : (
                  <FormattedMessage
                    id="auditLog.empty"
                    defaultMessage="No activity recorded yet"
                  />
                )}
              </EmptyState>
            ) : (
              <>
                <TableScroll
                  ref={tableScrollRef}
                  className="wide-scrollbar"
                >
                  <Table<AuditLogEntry>
                    rows={rows}
                    columns={columns}
                    loading={isFetching}
                    onRequestSort={() => {}}
                  />
                </TableScroll>
                <AuditLogPagination
                  page={page}
                  pageSize={AUDIT_LOG_PAGE_SIZE}
                  total={total}
                  onPageChange={setPage}
                />
              </>
            )}
          </Card>
        </Main>
      </div>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '100%',
  background: theme.palette.grey50,

  '> *': {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${NavigationHeight}px)`,
    // Only reached when the viewport is shorter than Main's min height.
    overflowY: 'auto',
  },
}));

// Fills the viewport so the card can shrink and scroll its rows internally,
// keeping the search and filters in view. Below the min height the page
// scrolls instead, so the rows never get squeezed to nothing.
const Main = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 560,
  width: '100%',
  padding: `0 ${theme.spacing(4)} ${theme.spacing(4)}`,
}));

const Header = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginTop: 40,
  marginBottom: theme.spacing(2),
}));

const Title = styled('div')(({ theme }) => ({
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  marginBottom: 8,
}));

const Subtitle = styled('div')(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.textSecondaryDarkGrey,
}));

const Card = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  background: theme.palette.white,
  border: `1px solid ${theme.palette.grey200}`,
  borderRadius: theme.radii.sm,
  overflow: 'hidden',

  table: {
    borderLeft: 0,
    borderRight: 0,
  },
}));

const Toolbar = styled('div')(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  padding: theme.spacing(1.75, 2.25),
}));

const TableScroll = styled('div')(({ theme }) => ({
  overflowY: 'auto',

  // The table's own top border and row borders scroll away under a sticky
  // header, leaving a see-through gap; `&&` beats Table's equally specific
  // rule regardless of style insertion order.
  '&& table': {
    borderTop: 0,
  },

  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },

  // Drawn on the header cells so the dividers move with the sticky header.
  'thead th': {
    boxShadow: `inset 0 1px 0 ${theme.palette.grey200}, inset 0 -1px 0 ${theme.palette.grey200}`,
  },
}));

const SearchWrapper = styled('div')({
  flex: 1,
  maxWidth: 520,
});

const SearchHint = styled('div')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.grey500,
  margin: theme.spacing(0.75, 0, 0, 2),
}));

const FilterWrapper = styled('div')(({ theme }) => ({
  width: 260,
  flexShrink: 0,
  // Lines up with the search field, whose TextField has the same top margin.
  marginTop: theme.spacing(1),

  // Select is sized for forms (48px tall, 350px min width, 16px text), which
  // overflows this wrapper; shrink it to match the small search field.
  '& > div': {
    minWidth: 0,
    height: 36,
  },
  '& [role="button"]': {
    padding: theme.spacing(0.75, 1, 0.75, 1.5),
    fontSize: 14,
  },
  '& [role="button"] div': {
    fontSize: 14,
  },
}));

const ActorRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
});

const Avatar = styled('div')(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'grid',
  placeItems: 'center',
  fontSize: 11,
  fontWeight: 600,
  background: theme.palette.grey100,
  color: theme.palette.grey600,

  '&[data-deleted="true"]': {
    background: 'transparent',
    border: `1.5px dashed ${theme.palette.grey300}`,
    color: theme.palette.grey500,
  },
}));

const CellText = styled('div')({
  minWidth: 0,
});

const PrimaryText = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.textPrimaryBlack,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const SecondaryText = styled('div')(({ theme }) => ({
  fontSize: 12,
  marginTop: 2,
  color: theme.palette.grey500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const DeletedActor = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontStyle: 'italic',
  color: theme.palette.grey500,
}));

const EmptyState = styled('div')(({ theme }) => ({
  padding: theme.spacing(7, 3),
  textAlign: 'center',
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.textPrimaryBlack,
  borderTop: `1px solid ${theme.palette.grey200}`,
}));

export default AuditLogAdminPage;
